import React, { useEffect, useState } from "react";
import axios from "axios";
import debounce from "lodash.debounce"; // Import lodash debounce
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Box,
  Pagination,
  TextField,
  Button,
} from "@mui/material";
import API_BASE_URL from "../config";

const AdminOffers = () => {
  const [offers, setOffers] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalOffers, setTotalOffers] = useState(0);
  const offersPerPage = 6;

  useEffect(() => {
    const fetchOffers = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/offers/alloffers`
        );
        const allOffers = response.data.offers || [];
        setOffers(allOffers);
        setTotalOffers(response.data.totalOffers || allOffers.length);
        setFilteredOffers(allOffers);
      } catch (error) {
        console.error("Error fetching offers:", error);
        alert("Failed to fetch offers.");
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  // Debounced search function to filter offers
  const handleSearch = debounce((query) => {
    const filtered = offers.filter((offer) =>
      (offer?.title || "").toLowerCase().includes(query.toLowerCase())
    );
    setFilteredOffers(filtered);
    setPage(1); // Reset pagination to the first page when search changes
  }, 500); // 500ms delay

  // This function updates the search query and triggers debounced search
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    handleSearch(e.target.value);
  };

  const handleStatusChange = async (id, newStatus) => {
    setOffers((prev) =>
      prev.map((offer) =>
        offer?.id === id ? { ...offer, status: newStatus } : offer
      )
    );

    try {
      const response = await axios.put(
        `${API_BASE_URL}/offers/${id}`,
        { status: newStatus },
        { headers: { "Content-Type": "application/json" } }
      );
      const updatedStatus = response.data?.offer?.status || newStatus;
      setOffers((prev) =>
        prev.map((offer) =>
          offer?.id === id ? { ...offer, status: updatedStatus } : offer
        )
      );
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status.");
    }
  };

  const handleDeleteOffer = async (id) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) return;

    try {
      await axios.delete(`${API_BASE_URL}/offers/${id}`);
      const updated = offers.filter((offer) => offer?.id !== id);
      setOffers(updated);
      setTotalOffers(updated.length);
    } catch (error) {
      console.error("Error deleting offer:", error);
      alert("Failed to delete offer.");
    }
  };

  const paginatedOffers = filteredOffers
    .filter((e) => e && typeof e === "object")
    .slice((page - 1) * offersPerPage, page * offersPerPage);

  if (loading) return <Typography>Loading offers...</Typography>;

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 4 }}>
      <Typography
        variant="h4"
        fontWeight="bold"
        textAlign="center"
        mb={3}
        color="#42326e"
      >
        Offers
      </Typography>

      <Box display="flex" justifyContent="right" mb={4}>
        <TextField
          label="Search by Offer Name"
          variant="outlined"
          value={searchQuery}
          onChange={handleSearchChange} // Updated to use debounced search
          fullWidth
          sx={{ maxWidth: 500 }}
        />
      </Box>

      {filteredOffers.length === 0 ? (
        <Typography textAlign="center">No offers found.</Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {paginatedOffers.map((offer, index) => (
              <Grid item xs={12} sm={6} md={4} key={offer?.id || index}>
                <Card
                  sx={{
                    height: "100%",
                    boxShadow: "1px 1px 2px 2px rgba(94, 238, 207, 0.6)",
                  }}
                >
                  <CardHeader
                    title={offer?.title || "Untitled Offer"}
                    titleTypographyProps={{ fontWeight: "bold" }}
                    subheader={
                      offer?.created_at
                        ? `Created on: ${new Date(
                            offer.created_at
                          ).toLocaleDateString()}`
                        : "Date unknown"
                    }
                  />

                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box>
                        <Typography>
                          <strong>Business Name:</strong>{" "}
                          {offer?.business || "N/A"}
                        </Typography>
                        <Typography>
                          <strong>Location:</strong> {offer?.location || "N/A"}
                        </Typography>
                        <Typography>
                          <strong>Description:</strong>{" "}
                          {offer?.description || "N/A"}
                        </Typography>
                        <Typography>
                          <strong>Original Price:</strong>{" "}
                          {offer?.original_price || "N/A"}
                        </Typography>
                        <Typography>
                          <strong>Discount Price:</strong>{" "}
                          {offer?.discount_price || "N/A"}
                        </Typography>
                        <Typography>
                          <strong>Category:</strong> {offer?.category || "N/A"}
                        </Typography>
                        <Typography>
                          <strong>Start Date:</strong>{" "}
                          {offer?.created_at
                            ? new Date(offer.created_at).toLocaleDateString()
                            : "N/A"}
                        </Typography>
                        <Typography>
                          <strong>End Date:</strong>{" "}
                          {offer?.expiry_date
                            ? new Date(offer.expiry_date).toLocaleDateString()
                            : "N/A"}
                        </Typography>
                        <Typography>
                          <strong>Status:</strong>{" "}
                          {offer?.status !== undefined
                            ? offer.status
                            : "Updating..."}
                        </Typography>
                      </Box>

                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                        ml={2}
                      >
                        <TextField
                          select
                          label="Status"
                          value={offer?.status || "inactive"}
                          onChange={(e) =>
                            handleStatusChange(offer.id, e.target.value)
                          }
                          SelectProps={{ native: true }}
                          size="small"
                          sx={{ mb: 1, width: 120 }}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </TextField>

                        <Button
                          onClick={() => handleDeleteOffer(offer.id)}
                          sx={{
                            backgroundColor: "red",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: 2,
                            cursor: "pointer",
                            width: 120,
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {filteredOffers.length > offersPerPage && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={Math.ceil(filteredOffers.length / offersPerPage)}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default AdminOffers;
